import apiClient from './client';
import type { 
  ResourceAllocation, 
  RdAllocation, 
  RdAllocationCandidate,
  RdBatchAllocatePayload,
  RdBatchAllocationResult,
  RdAllocationRecord,
  RdAllocationPlan,
  RdAllocationPlanPayload,
  VehicleAllocation, 
  Vehicle,
  ActualProject,
  ActualProjectAllocation,
  ActualProjectAutoAllocateResult,
  ActualProjectAllocationSaveItem,
  PaginatedResponse,
  FilterParams,
  TimesheetGenerateRequest,
  TimesheetGenerateResult,
  AsyncTaskStart,
  AsyncTaskStatus,
  TimesheetDetailRow,
  ProjectCandidate,
  ManualAllocateRequest,
} from '@/types';

export const allocationApi = {
  getResourceAllocations: async (params?: FilterParams): Promise<PaginatedResponse<ResourceAllocation>> => {
    const response = await apiClient.get<PaginatedResponse<ResourceAllocation>>('/allocations/project/', { params });
    return response.data;
  },

  createResourceAllocation: async (data: Partial<ResourceAllocation>): Promise<ResourceAllocation> => {
    const response = await apiClient.post<ResourceAllocation>('/allocations/project/', data);
    return response.data;
  },

  updateResourceAllocation: async (id: number, data: Partial<ResourceAllocation>): Promise<ResourceAllocation> => {
    const response = await apiClient.put<ResourceAllocation>(`/allocations/project/${id}`, data);
    return response.data;
  },

  deleteResourceAllocation: async (id: number): Promise<void> => {
    await apiClient.delete(`/allocations/project/${id}`);
  },

  exportProjectAllocations: async (params?: FilterParams): Promise<Blob> => {
    const response = await apiClient.get('/allocations/project/export', {
      params,
      responseType: 'blob',
    });
    return response.data;
  },

  getRdAllocations: async (params?: FilterParams): Promise<PaginatedResponse<RdAllocation>> => {
    const response = await apiClient.get<PaginatedResponse<RdAllocation>>('/allocations/rd/', { params });
    return response.data;
  },

  getRdAllocationCandidates: async (params: FilterParams): Promise<RdAllocationCandidate[]> => {
    const response = await apiClient.get<RdAllocationCandidate[]>('/allocations/rd/employees', { params });
    return response.data;
  },

  createRdBatchAllocation: async (data: RdBatchAllocatePayload): Promise<RdBatchAllocationResult> => {
    const response = await apiClient.post<RdBatchAllocationResult>('/allocations/rd/batch', data);
    return response.data;
  },

  getRdAllocationRecords: async (params: FilterParams): Promise<PaginatedResponse<RdAllocationRecord>> => {
    const response = await apiClient.get<PaginatedResponse<RdAllocationRecord>>('/allocations/rd/records', { params });
    return response.data;
  },

  exportRdAllocations: async (params: FilterParams): Promise<Blob> => {
    const response = await apiClient.get('/allocations/rd/export', {
      params,
      responseType: 'blob',
    });
    return response.data;
  },

  createRdAllocationPlan: async (data: RdAllocationPlanPayload): Promise<RdAllocationPlan> => {
    const response = await apiClient.post<RdAllocationPlan>('/allocations/rd/plan', data);
    return response.data;
  },

  getRdAllocationPlans: async (params?: { year?: number; company?: string; business_line?: string }): Promise<RdAllocationPlan[]> => {
    const response = await apiClient.get<RdAllocationPlan[]>('/allocations/rd/plans', { params });
    return response.data;
  },

  getRdAllocationPlan: async (planId: number): Promise<RdAllocationPlan> => {
    const response = await apiClient.get<RdAllocationPlan>(`/allocations/rd/plan/${planId}`);
    return response.data;
  },

  getLatestRdAllocationPlan: async (params: { year: number; company: string; business_line: string }): Promise<RdAllocationPlan | null> => {
    const response = await apiClient.get<RdAllocationPlan | null>('/allocations/rd/plan', { params });
    return response.data;
  },

  executeRdAllocationPlan: async (planId: number): Promise<RdBatchAllocationResult> => {
    const response = await apiClient.post<RdBatchAllocationResult>('/allocations/rd/execute', { plan_id: planId });
    return response.data;
  },

  resetRdAllocationPlan: async (planId: number): Promise<RdAllocationPlan> => {
    const response = await apiClient.post<RdAllocationPlan>(`/allocations/rd/plan/${planId}/reset`);
    return response.data;
  },

  createRdAllocation: async (data: Partial<RdAllocation>): Promise<RdAllocation> => {
    const response = await apiClient.post<RdAllocation>('/allocations/rd/', data);
    return response.data;
  },

  updateRdAllocation: async (id: number, data: Partial<RdAllocation>): Promise<RdAllocation> => {
    const response = await apiClient.put<RdAllocation>(`/allocations/rd/${id}`, data);
    return response.data;
  },

  deleteRdAllocation: async (id: number): Promise<void> => {
    await apiClient.delete(`/allocations/rd/${id}`);
  },

  getVehicleAllocations: async (params?: FilterParams): Promise<PaginatedResponse<VehicleAllocation>> => {
    const response = await apiClient.get<PaginatedResponse<VehicleAllocation>>('/allocations/vehicle/', { params });
    return response.data;
  },

  createVehicleAllocation: async (data: Partial<VehicleAllocation>): Promise<VehicleAllocation> => {
    const response = await apiClient.post<VehicleAllocation>('/allocations/vehicle/', data);
    return response.data;
  },

  updateVehicleAllocation: async (id: number, data: Partial<VehicleAllocation>): Promise<VehicleAllocation> => {
    const response = await apiClient.put<VehicleAllocation>(`/allocations/vehicle/${id}`, data);
    return response.data;
  },

  deleteVehicleAllocation: async (id: number): Promise<void> => {
    await apiClient.delete(`/allocations/vehicle/${id}`);
  },

  getVehicles: async (params?: FilterParams): Promise<Vehicle[]> => {
    const response = await apiClient.get<Vehicle[]>('/vehicles/', { params });
    return response.data;
  },

  getVehicleCatalog: async (params?: { start_month?: string; end_month?: string; require_vehicle?: boolean }): Promise<ActualProject[]> => {
    const response = await apiClient.get<ActualProject[]>('/projects/vehicle-catalog', { params });
    return response.data;
  },

  vehicleAllocate: async (params?: { start_month?: string; end_month?: string }): Promise<ActualProjectAutoAllocateResult> => {
    const response = await apiClient.post<ActualProjectAutoAllocateResult>('/projects/vehicle-allocate', null, { params });
    return response.data;
  },

  vehicleSaveStart: async (): Promise<AsyncTaskStart> => {
    const response = await apiClient.post<AsyncTaskStart>('/projects/vehicle-save');
    return response.data;
  },

  vehicleSaveStatus: async (taskId: string): Promise<AsyncTaskStatus> => {
    const response = await apiClient.get<AsyncTaskStatus>(`/projects/vehicle-save-status/${taskId}`);
    return response.data;
  },

  vehicleSavedList: async (params?: { search?: string; year?: number; page?: number; page_size?: number }): Promise<any> => {
    const response = await apiClient.get('/projects/vehicle-saved-list', { params });
    return response.data;
  },

  vehicleExport: async (items: ActualProjectAllocationSaveItem[]): Promise<Blob> => {
    const response = await apiClient.post('/projects/vehicle-export', { items }, {
      responseType: 'blob',
    });
    return response.data;
  },

autoAllocateActualProjects: async (allocationRatio?: number): Promise<ActualProjectAutoAllocateResult> => {
    const response = await apiClient.post<ActualProjectAutoAllocateResult>('/actual-project-allocations/auto-allocate', {}, {
      params: { allocation_ratio: allocationRatio || 35 },
    });
    return response.data;
  },

  resetActualProjectAllocations: async (): Promise<{ success: boolean; message: string; deleted_count: number }> => {
    const response = await apiClient.post<{ success: boolean; message: string; deleted_count: number }>('/actual-project-allocations/reset');
    return response.data;
  },

  saveActualProjectAllocations: async (items: ActualProjectAllocation[]): Promise<{ success: boolean; message: string; saved_count: number }> => {
    const response = await apiClient.post<{ success: boolean; message: string; saved_count: number }>('/actual-project-allocations/save', { items });
    return response.data;
  },

  getActualProjectAllocations: async (params?: FilterParams): Promise<PaginatedResponse<ActualProjectAllocation>> => {
    const response = await apiClient.get<PaginatedResponse<ActualProjectAllocation>>('/actual-project-allocations/', { params });
    return response.data;
  },

  updateActualProjectAllocation: async (id: number, data: Partial<Pick<ActualProjectAllocation, 'allocated_days' | 'allocated_amount' | 'allocated_gross_salary' | 'allocated_company_pension' | 'allocated_company_unemployment' | 'allocated_company_medical' | 'allocated_company_injury' | 'allocated_company_maternity' | 'allocated_company_housing_fund'>>): Promise<ActualProjectAllocation> => {
    const response = await apiClient.patch<ActualProjectAllocation>(`/actual-project-allocations/${id}`, data);
    return response.data;
  },

  exportActualProjectAllocations: async (params?: FilterParams): Promise<Blob> => {
    const response = await apiClient.get('/actual-project-allocations/export', {
      params,
      responseType: 'blob',
    });
    return response.data;
  },

  exportAllocationDiagnostics: async (allocationRatio?: number): Promise<Blob> => {
    const response = await apiClient.post('/actual-project-allocations/export-diagnostics', {}, {
      params: { allocation_ratio: allocationRatio || 35 },
      responseType: 'blob',
    });
    return response.data;
  },

  generateTimesheets: async (data: TimesheetGenerateRequest): Promise<AsyncTaskStart> => {
    const response = await apiClient.post<AsyncTaskStart>('/timesheets/generate', data);
    return response.data;
  },

  generateActualTimesheets: async (data: TimesheetGenerateRequest): Promise<AsyncTaskStart> => {
    const response = await apiClient.post<AsyncTaskStart>('/timesheets/generate-actual', data);
    return response.data;
  },

  getTimesheetTaskStatus: async (taskId: string): Promise<AsyncTaskStatus> => {
    const response = await apiClient.get<AsyncTaskStatus>(`/timesheets/task-status/${taskId}`);
    return response.data;
  },

  exportTimesheets: async (params?: FilterParams): Promise<Blob> => {
    const response = await apiClient.get('/timesheets/export', {
      params,
      responseType: 'blob',
    });
    return response.data;
  },

  getTimesheetDetailSummary: async (params?: FilterParams): Promise<PaginatedResponse<TimesheetDetailRow>> => {
    const response = await apiClient.get<PaginatedResponse<TimesheetDetailRow>>('/timesheets/detail-summary', { params });
    return response.data;
  },

  getRdTimesheetDetailSummary: async (params?: FilterParams): Promise<PaginatedResponse<TimesheetDetailRow>> => {
    const response = await apiClient.get<PaginatedResponse<TimesheetDetailRow>>('/timesheets/rd-detail-summary', { params });
    return response.data;
  },

  getProjectCandidates: async (businessLine: string, dateRange?: { start_year?: number; start_month?: number; end_year?: number; end_month?: number }): Promise<ProjectCandidate[]> => {
    const response = await apiClient.get<ProjectCandidate[]>('/actual-project-allocations/project-candidates', {
      params: { business_line: businessLine, ...dateRange },
    });
    return response.data;
  },

  manualAllocateActualProjects: async (data: ManualAllocateRequest): Promise<ActualProjectAutoAllocateResult> => {
    const response = await apiClient.post<ActualProjectAutoAllocateResult>('/actual-project-allocations/manual-allocate', data);
    return response.data;
  },

  aftersalesAllocate: async (params?: { start_month?: string; end_month?: string }): Promise<ActualProjectAutoAllocateResult> => {
    const response = await apiClient.post<ActualProjectAutoAllocateResult>('/projects/aftersales-allocate', null, { params });
    return response.data;
  },

  aftersalesSaveStart: async (): Promise<AsyncTaskStart> => {
    const response = await apiClient.post<AsyncTaskStart>('/projects/aftersales-save');
    return response.data;
  },

  aftersalesSaveStatus: async (taskId: string): Promise<AsyncTaskStatus> => {
    const response = await apiClient.get<AsyncTaskStatus>(`/projects/aftersales-save-status/${taskId}`);
    return response.data;
  },

  aftersalesSavedList: async (params?: { search?: string; year?: number; page?: number; page_size?: number }): Promise<any> => {
    const response = await apiClient.get('/projects/aftersales-saved-list', { params });
    return response.data;
  },
};
